/* eslint-disable react/prop-types */
import React, { useEffect } from "react";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

const createItem = () => ({
  bookingDate: "",
  documentNumber: "",
  documentDate: "",
  description: "",
  correspondingAccount: "",
  discountPeriod: "",
  debitIncurred: 0,
  creditIncurred: 0,
  balanceDebit: 0,
  balanceCredit: 0,
});

export function CustomerPaymentDetailsLedgerInfo({ formData }) {
  const { values, setFieldValue, containView } = formData;
  const { 
    details = [], 
    openingBalance = { debit: 0, credit: 0 } 
  } = values;

  const addItem = () => {
    setFieldValue("details", [...details, createItem()]);
  };

  const removeItem = (index) => {
    if (details.length <= 1) return;
    setFieldValue("details", details.filter((_, i) => i !== index));
  };

  const handleChangeItem = (index, field, val) => {
    const newDetails = [...details];
    newDetails[index][field] = val;
    setFieldValue("details", newDetails);
  };

  const num = (v) => (v && v !== "" && !isNaN(v) ? parseFloat(v) : 0);

  // Auto-calculation logic for Balances
  useEffect(() => {
    let currentDebit = num(openingBalance.debit);
    let currentCredit = num(openingBalance.credit);

    let totalDebitInc = 0;
    let totalCreditInc = 0;

    const newDetails = details.map((item) => {
      const dInc = num(item.debitIncurred);
      const cInc = num(item.creditIncurred);
      totalDebitInc += dInc;
      totalCreditInc += cInc;

      const netChange = dInc - cInc;
      const initialNet = currentDebit - currentCredit;
      const finalNet = initialNet + netChange;

      const dBal = finalNet > 0 ? finalNet : 0;
      const cBal = finalNet < 0 ? Math.abs(finalNet) : 0;

      currentDebit = dBal;
      currentCredit = cBal;

      return {
        ...item,
        balanceDebit: dBal,
        balanceCredit: cBal,
      };
    });

    const openingNet = num(openingBalance.debit) - num(openingBalance.credit);
    const totalNet = totalDebitInc - totalCreditInc;
    const finalNetResult = openingNet + totalNet;
    const closingDebit = finalNetResult > 0 ? finalNetResult : 0;
    const closingCredit = finalNetResult < 0 ? Math.abs(finalNetResult) : 0;

    const detailsChanged = JSON.stringify(newDetails) !== JSON.stringify(details);
    if (detailsChanged) {
      setFieldValue("details", newDetails);
      setFieldValue("totalIncurred", { debit: totalDebitInc, credit: totalCreditInc });
      setFieldValue("closingBalance", { debit: closingDebit, credit: closingCredit });
    }
  }, [
    JSON.stringify(details.map(i => [i.debitIncurred, i.creditIncurred])), 
    openingBalance.debit, openingBalance.credit
  ]);

  const s = {
    container: { fontFamily: '"Times New Roman", Times, serif', color: "#000", padding: "20px", backgroundColor: "#fff", fontSize: "14px" },
    headerTop: { display: "flex", justifyContent: "space-between", marginBottom: "15px" },
    titleBox: { textAlign: "center", margin: "10px 0" },
    inputDotted: { border: "none", borderBottom: "1px dotted #000", outline: "none", fontSize: "14px", fontFamily: "inherit", backgroundColor: "transparent", color: "#000", fontWeight: "bold" },
    inputDottedSmall: { border: "none", borderBottom: "1px dotted #000", outline: "none", width: "40px", fontSize: "14px", fontFamily: "inherit", textAlign: "center", backgroundColor: "transparent", color: "#000", fontWeight: "bold" },
    table: { width: "100%", borderCollapse: "collapse", fontSize: "13px", marginTop: "15px" },
    th: { border: "1px solid #000", padding: "8px 4px", textAlign: "center", fontWeight: "bold", verticalAlign: "middle", backgroundColor: "#fff" },
    td: { border: "1px solid #000", padding: 0, height: "35px", verticalAlign: "middle" },
    inputTable: { width: "100%", height: "100%", border: "none", outline: "none", padding: "0 4px", fontSize: "13px", fontFamily: "inherit", background: "transparent", color: "#000" },
    inputTableCenter: { width: "100%", height: "100%", border: "none", outline: "none", textAlign: "center", fontSize: "13px", fontFamily: "inherit", background: "transparent", color: "#000" },
    inputTableRight: { width: "100%", height: "100%", border: "none", outline: "none", textAlign: "right", padding: "0 4px", fontSize: "13px", fontFamily: "inherit", background: "transparent", color: "#000" },
    removeBtn: { border: "none", background: "transparent", color: "red", cursor: "pointer", fontWeight: "bold", fontSize: "16px" }
  };

  const fmt = (n) => (n && n !== 0 ? new Intl.NumberFormat("vi-VN").format(n) : "");

  return (
    <SoftBox style={s.container}>
      {/* HEADER */}
      <div style={s.headerTop}>
        <div style={{ width: "60%" }}>
          <div>Đơn vị: <input disabled={containView} style={{ ...s.inputDotted, width: "300px" }} value={values.unit} onChange={(e) => setFieldValue("unit", e.target.value)} /></div>
          <div>Địa chỉ: <input disabled={containView} style={{ ...s.inputDotted, width: "300px" }} value={values.address} onChange={(e) => setFieldValue("address", e.target.value)} /></div>
        </div>
        <div style={{ textAlign: "center", width: "40%" }}>
          <div style={{ fontWeight: "bold", fontSize: "15px" }}>Mẫu số S31-DN</div>
          <div style={{ fontStyle: "italic", fontSize: "13px" }}>(Kèm theo Thông tư số 99/2025/TT-BTC <br /> ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</div>
        </div>
      </div>

      {/* TITLE */}
      <div style={s.titleBox}>
        <h2 style={{ margin: 0, textTransform: "uppercase", fontSize: "18px" }}>SỔ CHI TIẾT THANH TOÁN VỚI NGƯỜI MUA (NGƯỜI BÁN)</h2>
        <div style={{ fontWeight: "bold", fontSize: "15px", marginTop: "5px" }}>(Dùng cho TK: 131, 331)</div>

        <div style={{ marginTop: 15, fontWeight: "bold" }}>
          Tài khoản: <input disabled={containView} style={{ ...s.inputDotted, width: "200px", textAlign: "center" }} value={values.accountNumber} onChange={(e) => setFieldValue("accountNumber", e.target.value)} />
        </div>
        <div style={{ marginTop: 5, fontWeight: "bold" }}>
          Đối tượng: <input disabled={containView} style={{ ...s.inputDotted, width: "200px", textAlign: "center" }} value={values.entityName} onChange={(e) => setFieldValue("entityName", e.target.value)} />
        </div>
        <div style={{ marginTop: 5, fontWeight: "bold" }}>
          Loại tiền: VNĐ
        </div>
      </div>

      {/* TABLE */}
      <div style={{ overflowX: "auto" }}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={{ ...s.th, width: "80px" }} rowSpan={2}>Ngày, tháng ghi sổ</th>
              <th style={s.th} colSpan={2}>Chứng từ</th>
              <th style={{ ...s.th, width: "250px" }} rowSpan={2}>Diễn giải</th>
              <th style={{ ...s.th, width: "60px" }} rowSpan={2}>TK đối ứng</th>
              <th style={{ ...s.th, width: "80px" }} rowSpan={2}>Thời hạn được chiết khấu</th>
              <th style={s.th} colSpan={2}>Số phát sinh</th>
              <th style={s.th} colSpan={2}>Số dư</th>
              {!containView && <th style={{ ...s.th, width: "30px" }} rowSpan={2}>#</th>}
            </tr>
            <tr>
              <th style={{ ...s.th, width: "70px" }}>Số hiệu</th>
              <th style={{ ...s.th, width: "80px" }}>Ngày, tháng</th>
              <th style={{ ...s.th, width: "100px" }}>Nợ</th>
              <th style={{ ...s.th, width: "100px" }}>Có</th>
              <th style={{ ...s.th, width: "100px" }}>Nợ</th>
              <th style={{ ...s.th, width: "100px" }}>Có</th>
            </tr>
            <tr style={{ backgroundColor: "#f0f0f0" }}>
              <th style={s.th}>A</th>
              <th style={s.th}>B</th>
              <th style={s.th}>C</th>
              <th style={s.th}>D</th>
              <th style={s.th}>E</th>
              <th style={s.th}>1</th>
              <th style={s.th}>2</th>
              <th style={s.th}>3</th>
              <th style={s.th}>4</th>
              <th style={s.th}>5</th>
              {!containView && <th style={s.th}></th>}
            </tr>
          </thead>
          <tbody>
            {/* Opening Balance Row */}
            <tr style={{ fontWeight: "bold" }}>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={{ ...s.td, padding: "0 8px" }}>- Số dư đầu kỳ</td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={openingBalance.debit} onChange={(e) => setFieldValue("openingBalance.debit", e.target.value)} /></td>
              <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={openingBalance.credit} onChange={(e) => setFieldValue("openingBalance.credit", e.target.value)} /></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* details Row */}
            {(details || []).map((item, index) => (
              <tr key={index}>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.bookingDate} onChange={(e) => handleChangeItem(index, "bookingDate", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.documentNumber} onChange={(e) => handleChangeItem(index, "documentNumber", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.documentDate} onChange={(e) => handleChangeItem(index, "documentDate", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTable} value={item.description} onChange={(e) => handleChangeItem(index, "description", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.correspondingAccount} onChange={(e) => handleChangeItem(index, "correspondingAccount", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} style={s.inputTableCenter} value={item.discountPeriod} onChange={(e) => handleChangeItem(index, "discountPeriod", e.target.value)} /></td>
                
                <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={item.debitIncurred} onChange={(e) => handleChangeItem(index, "debitIncurred", e.target.value)} /></td>
                <td style={s.td}><input disabled={containView} type="number" style={s.inputTableRight} value={item.creditIncurred} onChange={(e) => handleChangeItem(index, "creditIncurred", e.target.value)} /></td>
                
                <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(item.balanceDebit)}</td>
                <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(item.balanceCredit)}</td>

                {!containView && (
                  <td style={{ ...s.td, textAlign: "center" }}>
                    <button type="button" style={s.removeBtn} onClick={() => removeItem(index)}>–</button>
                  </td>
                )}
              </tr>
            ))}

            {/* Total Incurred Row */}
            <tr style={{ backgroundColor: "#fdfdfd", fontWeight: "bold" }}>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={{ ...s.td, padding: "0 8px" }}>- Cộng số phát sinh</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(values?.totalIncurred?.debit)}</td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(values?.totalIncurred?.credit)}</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              {!containView && <td style={s.td}></td>}
            </tr>

            {/* Closing Balance Row */}
            <tr style={{ backgroundColor: "#fafafa", fontWeight: "bold" }}>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={s.td}></td>
              <td style={{ ...s.td, padding: "0 8px" }}>- Số dư cuối kỳ</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "center" }}>x</td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(values?.closingBalance?.debit)}</td>
              <td style={{ ...s.td, textAlign: "right", padding: "0 4px" }}>{fmt(values?.closingBalance?.credit)}</td>
              {!containView && <td style={s.td}></td>}
            </tr>
          </tbody>
        </table>

        {!containView && (
          <SoftBox mt={2} textAlign="center">
            <SoftButton variant="outlined" color="dark" onClick={addItem}>+ Thêm dòng chi tiết</SoftButton>
          </SoftBox>
        )}
      </div>

      {/* FOOTER */}
      <div style={{ marginTop: "25px" }}>
        <div style={{ marginBottom: "5px" }}>- Sổ này có <input disabled={containView} style={s.inputDottedSmall} value={values.totalPages} onChange={(e) => setFieldValue("totalPages", e.target.value)} /> trang, đánh số từ trang 01 đến trang...</div>
        <div>- Ngày mở sổ: <input disabled={containView} style={{ ...s.inputDotted, width: "150px" }} value={values.openingDate} onChange={(e) => setFieldValue("openingDate", e.target.value)} /></div>
      </div>

      {/* SIGNATURES BLOCK (Commented out as requested) */}
      {/* 
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "30px", textAlign: "center" }}>
        <div style={{ width: "30%" }}>
          <b style={{ textTransform: "none" }}>Người ghi sổ</b><br/>
          <span style={{ fontStyle: "italic", fontSize: "13px" }}>(Ký, họ tên)</span>
          <SoftBox mt={8}>
            <input disabled={containView} style={{ ...s.inputDotted, width: "150px", textAlign: "center" }} value={values.ledgerClerk} onChange={(e) => setFieldValue("ledgerClerk", e.target.value)} />
          </SoftBox>
        </div>
        <div style={{ width: "30%" }}>
          <b style={{ textTransform: "none" }}>Kế toán trưởng</b><br/>
          <span style={{ fontStyle: "italic", fontSize: "13px" }}>(Ký, họ tên)</span>
          <SoftBox mt={8}>
            <input disabled={containView} style={{ ...s.inputDotted, width: "150px", textAlign: "center" }} value={values.chiefAccountant} onChange={(e) => setFieldValue("chiefAccountant", e.target.value)} />
          </SoftBox>
        </div>
        <div style={{ width: "40%" }}>
          <div style={{ fontStyle: "italic", fontSize: "14px", marginBottom: "5px" }}>
            Ngày <input disabled={containView} style={s.inputDottedSmall} value={values.signingDay} onChange={(e) => setFieldValue("signingDay", e.target.value)} /> tháng <input disabled={containView} style={s.inputDottedSmall} value={values.signingMonth} onChange={(e) => setFieldValue("signingMonth", e.target.value)} /> năm <input disabled={containView} style={{ ...s.inputDottedSmall, width: "50px" }} value={values.signingYear} onChange={(e) => setFieldValue("signingYear", e.target.value)} />
          </div>
          <b style={{ textTransform: "none" }}>Người đại diện theo pháp luật</b><br/>
          <span style={{ fontStyle: "italic", fontSize: "13px" }}>(Ký, họ tên, đóng dấu)</span>
          <SoftBox mt={8}>
            <input disabled={containView} style={{ ...s.inputDotted, width: "150px", textAlign: "center" }} value={values.legalRepresentative} onChange={(e) => setFieldValue("legalRepresentative", e.target.value)} />
          </SoftBox>
        </div>
      </div> 
      */}
    </SoftBox>
  );
}
